import { Button, Grid } from '@mui/material';
import { AxiosError } from 'axios';
import { Form, Formik, FormikHelpers } from 'formik';
import { map } from 'lodash';
import React, { Fragment, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { useNavigate, useParams } from 'react-router-dom';
import * as Yup from 'yup';

import { groupsApi, operatorsApi, shopsApi, tradersApi } from 'api';
import {
  CopyText,
  DataWrapper,
  FormControls,
  FormikCheckbox,
  FormikNumericField,
  FormikNumericTextField,
  FormikTextField,
  ListFilter,
  PageHeader,
  SelectionList,
} from 'components';
import { NEW_ID } from 'constants/common.constants';
import { ROUTE_PATH } from 'constants/routes';
import { QueryKey } from 'enums';
import { useMutation, useShopsQuery, useUser } from 'hooks';
import { TranslationNamespace } from 'i18n';
import { Group, Operator, Shop, Trader } from 'types';
import { validationUtils } from 'utils';

type Values = Pick<
  Group,
  'name' | 'payin' | 'payout' | 'payoutOrdersAccessTimeout' | 'telegramChatId'
>;

export const GroupDetailsPage: React.FC = () => {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { role, isAdmin, isTechOperator } = useUser();

  // TODO: move translations to common
  const { t } = useTranslation(TranslationNamespace.Admin, {
    keyPrefix: 'pages.group_details',
  });
  const { t: tCommon } = useTranslation(TranslationNamespace.Common);

  const [initialValues, setInitialValues] = useState<Values>({
    name: '',
    payin: false,
    payout: false,
    payoutOrdersAccessTimeout: 0,
    telegramChatId: null,
  });

  const [traders, setTraders] = useState<Trader[]>([]);
  const [operators, setOperators] = useState<Operator[]>([]);
  const [shops, setShops] = useState<Shop[]>([]);

  const [selectedTraderIds, setSelectedTraderIds] = useState<string[]>([]);
  const [selectedShopIds, setSelectedShopIds] = useState<string[]>([]);
  const [selectedOperatorIds, setSelectedOperatorIds] = useState<string[]>([]);

  const [sortTraderIds, setSortTraderIds] = useState<string[]>([]);
  const [sortShopIds, setSortShopIds] = useState<string[]>([]);
  const [sortOperatorIds, setSortOperatorIds] = useState<string[]>([]);

  const isNew = useMemo(() => id === NEW_ID, [id]);
  const title = useMemo(
    () => (isNew ? t('title_create') : t('title_edit')),
    [t, isNew],
  );

  const backUrl = useMemo(() => {
    if (isAdmin) {
      return ROUTE_PATH.ADMIN.GROUPS;
    } else if (isTechOperator) {
      return ROUTE_PATH.TECH_OPERATOR.GROUPS;
    }
  }, [isAdmin, isTechOperator]);

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        name: Yup.string().required(tCommon('errors.required')),
        payoutOrdersAccessTimeout: Yup.number().min(0),
      }),
    [tCommon],
  );

  const traderLabelGetter = useCallback(
    (trader: Trader) => trader?.user?.name ?? '',
    [],
  );
  const shopLabelGetter = useCallback((shop: Shop) => shop.name, []);
  const operatorLabelGetter = useCallback(
    (operator: Operator) => operator?.user?.name ?? '',
    [],
  );

  const setInitialValuesFromGroup = useCallback((group: Group) => {
    setInitialValues({
      name: group?.name,
      payin: group.payin,
      payout: group.payout,
      payoutOrdersAccessTimeout: group.payoutOrdersAccessTimeout,
      telegramChatId: group.telegramChatId,
    });
    const traderIds = map(group.traders, (trader) => trader.id || '');
    const shopIds = map(group.shops, (shop) => shop.id || '');
    const operatorIds = map(group.operators, (operator) => operator.id || '');
    setSelectedTraderIds(traderIds);
    setSelectedShopIds(shopIds);
    setSelectedOperatorIds(operatorIds);
    setSortTraderIds(traderIds);
    setSortShopIds(shopIds);
    setSortOperatorIds(operatorIds);
  }, []);

  const queryResultGroup = useQuery(
    [QueryKey.Groups, id],
    () => groupsApi.getOne(id),
    {
      enabled: !isNew,
      onSuccess: setInitialValuesFromGroup,
    },
  );

  const queryResultTraders = useQuery(
    QueryKey.Traders,
    () => tradersApi.getAllAsRole(role)(),
    {
      onSuccess: setTraders,
    },
  );

  const queryResultShops = useShopsQuery(
    QueryKey.Shops,
    shopsApi.getAllAsRole(role),
    {
      onSuccess: setShops,
    },
  );

  const queryResultOperators = useQuery(
    QueryKey.Operators,
    operatorsApi.getAll,
    {
      onSuccess: setOperators,
    },
  );

  const queryResults = useMemo(() => {
    const queries = [
      queryResultGroup,
      queryResultTraders,
      queryResultShops,
      queryResultOperators,
    ];
    return queries;
  }, [
    queryResultGroup,
    queryResultShops,
    queryResultTraders,
    queryResultOperators,
  ]);

  const { mutate: createGroup } = useMutation<
    Group,
    AxiosError,
    Partial<Group>,
    unknown
  >(groupsApi.create);

  const { mutate: updateGroup } = useMutation<
    Group,
    AxiosError,
    { id: string; data: Partial<Group> },
    unknown
  >(groupsApi.update);

  const handleSubmit = useCallback(
    (values: Values, formikHelpers: FormikHelpers<Values>) => {
      const options = {
        onSuccess: () => {
          navigate(backUrl!);
        },
        onError: (error: AxiosError) => {
          formikHelpers.setErrors(validationUtils.getFormErrors(error));
        },
        onSettled: () => formikHelpers.setSubmitting(false),
      };

      const mappedValues = {
        ...values,
        telegramChatId: values.telegramChatId || null,
        traders: map(selectedTraderIds, (traderId) => ({ id: traderId })),
        shops: map(selectedShopIds, (shopId) => ({ id: shopId })),
        operators: map(selectedOperatorIds, (operatorId) => ({
          id: operatorId,
        })),
      };

      if (isNew) {
        createGroup(mappedValues, options);
      } else {
        updateGroup({ id, data: mappedValues }, options);
      }
    },
    [
      id,
      isNew,
      backUrl,
      navigate,
      createGroup,
      updateGroup,
      selectedTraderIds,
      selectedShopIds,
      selectedOperatorIds,
    ],
  );

  return (
    <Fragment>
      <PageHeader title={title}></PageHeader>
      <DataWrapper queryResult={queryResults} ignoreState={{ empty: isNew }}>
        <Formik
          initialValues={initialValues}
          enableReinitialize
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {(formik) => (
            <Form>
              <div className="tw-mb-6">
                <div className="tw-text-xl tw-mb-4">{t('general_info')}</div>
                <FormControls>
                  {!isNew && (
                    <CopyText
                      className="tw-mb-4"
                      text={queryResultGroup.data?.id || ''}
                    />
                  )}
                  <FormikTextField
                    label={t('fields.name')}
                    name="name"
                    type="text"
                    fullWidth
                    required
                    sx={{ maxWidth: 400 }}
                  />
                  <FormikNumericTextField
                    label={t('fields.telegram_chat_id')}
                    name="telegramChatId"
                    fullWidth
                    sx={{ maxWidth: 400 }}
                    decimalScale={0}
                    thousandSeparator=""
                  />
                  <div>
                    <FormikCheckbox name="payin" label={t('fields.payin')} />
                    <FormikCheckbox name="payout" label={t('fields.payout')} />
                  </div>
                  <FormikNumericField
                    label={t('fields.payout_orders_delay')}
                    name="payoutOrdersAccessTimeout"
                    fullWidth
                    sx={{ maxWidth: 400 }}
                    suffix={tCommon('suffixes.minutes')}
                    allowNegative={false}
                    decimalScale={0}
                    defaultValue={0}
                    helperText={t('fields.payout_orders_delay_description')}
                  />
                </FormControls>
              </div>
              <Grid container spacing={6}>
                <Grid size={{ xs: 12, md: 4 }} sx={{ display: 'flex' }}>
                  <ListFilter items={traders} labelGetter={traderLabelGetter}>
                    {(items, renderInput) => (
                      <SelectionList
                        title={t('fields.traders')}
                        items={items}
                        selectedIds={selectedTraderIds}
                        sortByIds={sortTraderIds}
                        onSelectedIdsChange={setSelectedTraderIds}
                        renderInput={renderInput}
                        labelGetter={traderLabelGetter}
                        noSelectionText={t('no_selection')}
                      />
                    )}
                  </ListFilter>
                </Grid>
                <Grid size={{ xs: 12, md: 4 }} sx={{ display: 'flex' }}>
                  <ListFilter items={shops} labelGetter={shopLabelGetter}>
                    {(items, renderInput) => (
                      <SelectionList
                        title={t('fields.shops')}
                        items={items}
                        selectedIds={selectedShopIds}
                        sortByIds={sortShopIds}
                        onSelectedIdsChange={setSelectedShopIds}
                        renderInput={renderInput}
                        labelGetter={shopLabelGetter}
                        noSelectionText={t('no_selection')}
                      />
                    )}
                  </ListFilter>
                </Grid>
                <Grid size={{ xs: 12, md: 4 }} sx={{ display: 'flex' }}>
                  <ListFilter
                    items={operators}
                    labelGetter={operatorLabelGetter}
                  >
                    {(items, renderInput) => (
                      <SelectionList
                        title={t('fields.operators')}
                        items={items}
                        selectedIds={selectedOperatorIds}
                        sortByIds={sortOperatorIds}
                        onSelectedIdsChange={setSelectedOperatorIds}
                        renderInput={renderInput}
                        labelGetter={operatorLabelGetter}
                        noSelectionText={t('no_selection')}
                      />
                    )}
                  </ListFilter>
                </Grid>
              </Grid>
              <div className="tw-mt-8">
                <Button
                  type="submit"
                  variant="outlined"
                  disabled={formik.isSubmitting}
                  onClick={formik.submitForm}
                >
                  {tCommon('buttons.save')}
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      </DataWrapper>
    </Fragment>
  );
};
